import React, { memo } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import {
  Alert,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { PrimaryButton } from '../../../../components/Button/ActionButtons';
import { NEW_SITE_ID, effectiveDeployTarget, resolveSuggestedSite } from '../../../utils/webflowDeployer';
import {
  BUSY_PHASES,
  DEPLOY_PHASES,
  ItemPhase,
  isItemReady,
} from '../hooks/useDeployQueue';
import texts from '../texts.json';
import {
  ItemCard,
  ItemHeader,
  FileName,
  Meta,
  TargetRow,
} from '../styles';
import SitePicker from './SitePicker';
import DeployProgress from './DeployProgress';
import MessageAlert from './MessageAlert';

/**
 * One picked ZIP, from upload through inspection to its deploy.
 */
const DeployItem = ({
  item,
  sites,
  isLoadingSites,
  sitesError,
  onTargetChange,
  onDeploy,
  onRemove,
}) => {
  const {
    runId,
    fileName,
    storagePath,
    phase,
    uploadProgress,
    inspection,
    error,
    deployError,
    result,
  } = item;
  const validation = inspection?.validation;
  const { selectedSiteId, newSiteName } = effectiveDeployTarget(item, sites);

  const renderInspection = () => (
    <>
      <Meta>
        {format(texts.item.files, { count: validation.fileCount })}
        {' · '}
        {validation.formsFound > 0
          ? format(texts.item.forms, { count: validation.formsFound })
          : texts.item.noForms}
      </Meta>
      <MessageAlert severity="error" title={texts.item.invalid} messages={validation.errors} />
      <MessageAlert severity="warning" title={texts.item.warnings} messages={validation.warnings} />
      {validation.isValid && (
        <TargetRow>
          <SitePicker
            sites={sites}
            isLoading={isLoadingSites}
            loadError={sitesError}
            suggestedSite={resolveSuggestedSite(inspection, sites)}
            selectedSiteId={selectedSiteId}
            onChange={(siteId) => onTargetChange(runId, { selectedSiteId: siteId })}
          />
          {selectedSiteId === NEW_SITE_ID && (
            <TextField
              label={texts.item.newSiteName}
              value={newSiteName}
              onChange={(event) => onTargetChange(runId, { newSiteName: event.target.value })}
              size="small"
            />
          )}
          <PrimaryButton
            variant="light"
            size="medium"
            $fitToContent
            disabled={!isItemReady(item, sites)}
            onClick={() => onDeploy(runId)}
          >
            {texts.item.deploy}
          </PrimaryButton>
        </TargetRow>
      )}
      {!!deployError && <Alert severity="error">{deployError}</Alert>}
    </>
  );

  const renderBody = () => {
    if (DEPLOY_PHASES.includes(phase)) {
      return (
        <DeployProgress
          runId={runId}
          storagePath={storagePath}
          phase={phase}
          result={result}
          deployError={deployError}
        />
      );
    }
    switch (phase) {
      case ItemPhase.UPLOADING:
        return (
          <>
            <Meta>{format(texts.item.uploading, { progress: Math.round(uploadProgress) })}</Meta>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </>
        );
      case ItemPhase.INSPECTING:
        return (
          <>
            <Meta>{texts.item.inspecting}</Meta>
            <LinearProgress />
          </>
        );
      case ItemPhase.READY:
        return renderInspection();
      default:
        return <Alert severity="error">{error}</Alert>;
    }
  };

  return (
    <ItemCard>
      <ItemHeader>
        <FileName>{fileName}</FileName>
        <Tooltip title={texts.item.remove}>
          <span>
            <IconButton
              aria-label={texts.item.remove}
              size="small"
              disabled={BUSY_PHASES.includes(phase)}
              onClick={() => onRemove(runId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ItemHeader>
      {renderBody()}
    </ItemCard>
  );
};

DeployItem.propTypes = {
  item: PropTypes.shape({
    runId: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    storagePath: PropTypes.string.isRequired,
    phase: PropTypes.oneOf(Object.values(ItemPhase)).isRequired,
    uploadProgress: PropTypes.number,
    inspection: PropTypes.shape({
      validation: PropTypes.shape({
        isValid: PropTypes.bool,
        errors: PropTypes.arrayOf(PropTypes.string),
        warnings: PropTypes.arrayOf(PropTypes.string),
        fileCount: PropTypes.number,
        formsFound: PropTypes.number,
      }),
      suggestedSite: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
    selectedSiteId: PropTypes.string,
    newSiteName: PropTypes.string,
    error: PropTypes.string,
    deployError: PropTypes.string,
    result: PropTypes.object,
  }).isRequired,
  sites: PropTypes.array.isRequired,
  isLoadingSites: PropTypes.bool.isRequired,
  sitesError: PropTypes.string,
  onTargetChange: PropTypes.func.isRequired,
  onDeploy: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

DeployItem.defaultProps = {
  sitesError: '',
};

// Memoised: upload progress re-renders the page, and only the uploading row has changed.
export default memo(DeployItem);
